// SETTINGS V2 — Profil page (SETTINGS_V2_SP2 §5/§6). First data-bound v2 page:
// replaces the SP0 ProfileGhostV2 body with the real profile form + Security card
// + the portalled change-password dialog.
//
// DATA PARITY (BINDING, §4.1): PUT body is EXACTLY
// { firstName, lastName, phoneNumber, department } via the Customers proxy
// (customerAccountSlice.updateAccount → customersApi) — email + companyName are
// display-only, NOT in the payload.
//
// USER-APPROVED IN-SP2 FIXES (Q-SETV2-7 / Q-SETV2-8, override the spec's "keep
// the warts" note): on a FULFILLED PUT only, mirror the phone to the JWT slice
// (updateUser) AND refetch getAccount() so identity (avatar / rail / eyebrow)
// refreshes and the dirty-seed resets (Save disables post-save). On a rejected
// PUT: neither dispatch fires (error toast only). No slice edits; no MUI/sx/dt-tokens.
import React, { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { phoneRegex } from "../../../../shared/validators";
import ActionToast from "../../../../shared/components/ActionToast";
import Loader from "../../../../shared/components/Loader";
import { getAccount, clearError, updateAccount, setOpenSnackbar } from "../../customerAccountSlice";
import { updateUser } from "../../../../account/accountSlice";
import { openDialog } from "../settingsV2UiSlice";
import { SCard, SInputField, SSaveBtn, SComingPill } from "../atoms";
import ChangePasswordDialogV2 from "../dialogs/ChangePasswordDialogV2";

const ellipsis: React.CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const ProfilePageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: tAccount } = useTranslation("account");
  const { translate: tSettings } = useTranslation("settings");
  const { isPhone } = useBreakpoint();

  const { isLoading, isSaving, userInfo, openSnackbar, alertMessage, severity } =
    useAppSelector((s) => s.customerAccount);

  const [form, setForm] = useState({ firstName: "", lastName: "", phoneNumber: "", department: "" });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (f: string) => setTouched((t) => ({ ...t, [f]: true }));

  // Mount: fetch account; unmount: clearError (legacy lines 69-74). The duplicate
  // GET on first landing (SettingsV2Layout also fetches) is PARITY.
  useEffect(() => {
    dispatch(getAccount());
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Seed the controlled form from userInfo (mirrors legacy reset() effect).
  useEffect(() => {
    if (userInfo) {
      setForm({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        phoneNumber: userInfo.phoneNumber ?? "",
        department: userInfo.department ?? "",
      });
      setTouched({});
    }
  }, [userInfo]);

  const setField = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Validity — legacy isFormValid verbatim (lines 48-53).
  const isFormValid =
    form.firstName.trim() !== "" &&
    form.lastName.trim() !== "" &&
    (!form.phoneNumber || phoneRegex.test(form.phoneNumber));

  // Dirty — controlled equivalent of RHF isDirty (compare against the seed).
  const isDirty =
    !!userInfo &&
    (form.firstName !== userInfo.firstName ||
      form.lastName !== userInfo.lastName ||
      form.phoneNumber !== (userInfo.phoneNumber ?? "") ||
      form.department !== (userInfo.department ?? ""));

  const disableSave = isSaving || !isFormValid || !isDirty;

  // Save flow (§6.2 dispatch order preserved; Q-SETV2-7/8 fixes layered on).
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disableSave) return;
    const result = await dispatch(
      updateAccount({
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
        department: form.department,
      })
    ); // 1) PUT /v1/account — EXACTLY these 4 keys
    // Q-SETV2-8: guard the second dispatch on a fulfilled PUT (no updateUser /
    // no refetch on failure). Q-SETV2-7: refetch to refresh identity + re-seed.
    if (updateAccount.fulfilled.match(result)) {
      dispatch(updateUser({ phoneNumber: form.phoneNumber })); // 2) local JWT-mirror
      dispatch(getAccount()); // 3) refresh identity + reset dirty seed
    }
  };

  if (isLoading) return <Loader />;

  // Initials — legacy verbatim: '?' ONLY when userInfo is null (§5.1).
  const initials = userInfo
    ? `${(userInfo.firstName || "").charAt(0)}${(userInfo.lastName || "").charAt(0)}`.toUpperCase()
    : "?";

  const firstNameError =
    touched.firstName && form.firstName.trim() === "" ? tAccount("fieldRequired") : undefined;
  const lastNameError =
    touched.lastName && form.lastName.trim() === "" ? tAccount("fieldRequired") : undefined;
  const phoneError =
    touched.phoneNumber && !!form.phoneNumber && !phoneRegex.test(form.phoneNumber)
      ? tAccount("invalidPhoneNumber")
      : undefined;

  return (
    // Page root: NO animation (cards animate individually — a root transform
    // would trap the fixed-position ActionToast).
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0, flex: 1 }}>
      <ActionToast
        open={openSnackbar}
        variant={severity}
        message={alertMessage ? tAccount(alertMessage) : ""}
        onClose={() => dispatch(setOpenSnackbar(false))}
        duration={6000}
      />

      {/* ─── Personal Information ─── */}
      <SCard
        title={tSettings("settings:profile.title")}
        action={
          <SSaveBtn
            type="submit"
            form="settingsProfileFormV2"
            enabled={!disableSave}
            busy={isSaving}
            label={isSaving ? tAccount("saving") : tAccount("saveChanges")}
          />
        }
        style={{ animation: "sv2-card-reveal 0.4s ease-out both" }}
      >
        {/* Identity row (§5.1) */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span
            style={{
              width: 52, height: 52, borderRadius: 999,
              background: "var(--mod-accent)", color: "#fff",
              display: "grid", placeItems: "center",
              fontSize: 17, fontWeight: 700, flexShrink: 0,
            }}
          >
            {initials}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)", ...ellipsis }}>
              {userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : ""}
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)", marginTop: 1, ...ellipsis }}>
              {userInfo?.email || ""}
            </div>
          </div>
        </div>

        {/* The <form> exists so Enter-in-a-field submits; the SSaveBtn lives in
            the card action slot (outside the form) and links via `form` (§5.2). */}
        <form id="settingsProfileFormV2" onSubmit={handleSave}>
          <div style={{ display: "grid", gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div style={{ minWidth: 0 }} onBlur={() => markTouched("firstName")}>
              <SInputField
                label={tAccount("firstName")} required
                value={form.firstName} onChange={setField("firstName")}
                placeholder={tAccount("firstNamePlaceholder")} error={firstNameError}
              />
            </div>
            <div style={{ minWidth: 0 }} onBlur={() => markTouched("lastName")}>
              <SInputField
                label={tAccount("lastName")} required
                value={form.lastName} onChange={setField("lastName")}
                placeholder={tAccount("lastNamePlaceholder")} error={lastNameError}
              />
            </div>
            <SInputField label={tAccount("email")} disabled mono value={userInfo?.email || ""} onChange={() => {}} />
            <div style={{ minWidth: 0 }} onBlur={() => markTouched("phoneNumber")}>
              <SInputField
                label={tAccount("phoneNumber")} type="tel" mono
                value={form.phoneNumber} onChange={setField("phoneNumber")}
                placeholder={tAccount("phoneNumberPlaceholder")} error={phoneError}
              />
            </div>
            <SInputField
              label={tAccount("department")} value={form.department}
              onChange={setField("department")} placeholder={tAccount("departmentPlaceholder")}
            />
            <SInputField label={tAccount("company")} disabled value={userInfo?.companyName || ""} onChange={() => {}} />
          </div>
        </form>
      </SCard>

      {/* ─── Security ─── */}
      <SCard
        title={tSettings("settings:profile.security")}
        style={{ animation: "sv2-card-reveal 0.4s ease-out 0.1s both" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
              {tSettings("settings:profile.passwordLabel")}
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--faint)", marginTop: 2 }}>
              {tSettings("settings:profile.passwordMask")}
            </div>
          </div>
          <button
            type="button" className="btn"
            onClick={() => dispatch(openDialog("changePassword"))}
            style={{ whiteSpace: "nowrap" }}
          >
            {tSettings("settings:profile.changePassword")}
          </button>
        </div>
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10,
            marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-soft)",
          }}
        >
          <div style={{ flex: 1, fontSize: 13, color: "var(--ink-2)" }}>
            {tSettings("settings:profile.twoFactor")}
          </div>
          <SComingPill label={tSettings("settings:profile.comingSoon")} />
        </div>
      </SCard>

      {/* Portalled change-password dialog (reads its own open-state). */}
      <ChangePasswordDialogV2 />
    </div>
  );
};

export default ProfilePageV2;
